import Module from '../../module';
import scroll from '../../../libs/scroll/scroll';
import HighchartsStock from 'highcharts/highstock';
import 'highcharts/modules/accessibility';

type Ticker = 'plsrl' | 'plsrv' | 'psrhf';

interface OptionsInterface {
	cssId: {
		anim?: {
			investorsStockInfoChartHeaderCls: string;
			investorsStockInfoChartWrapperCls: string;
		},

		investorsStockInfoChartCanvasId?: string;
		investorsStockInfoChartWrapperCls?: string;
		investorsStockInfoChartWrapperLoadingCls?: string;
		investorsStockInfoChartButtonCls?: string;
		chartStockButtonActiveCls?: string;
	},

	apiEndpointPlsrl?: string;
	apiEndpointPlsrv?: string;
	apiEndpointPsrhf?: string;
	slug: string;
}

class Chart extends Module {

	publicVars: OptionsInterface;
	privateVars: {
		chart?: HighchartsStock.StockChart;
		tickers: ['plsrl', 'plsrv', 'psrhf'];
		currentTicker: Ticker;
		plsrl: {
			data: [number, number][],
			volume: [number, number][]
		},
		plsrv: {
			data: [number, number][],
			volume: [number, number][]
		},
		psrhf: {
			data: [number, number][],
			volume: [number, number][]
		},
	} = {
		currentTicker: 'plsrl',
		tickers: ['plsrl', 'plsrv', 'psrhf'],
		plsrl: {
			data: [],
			volume: []
		},
		plsrv: {
			data: [],
			volume: []
		},
		psrhf: {
			data: [],
			volume: []
		},
	};

	constructor(options: OptionsInterface) {
		super('Investors Stock Info Chart Module');

		this.publicVars = options;
	}

	startup() {
		this.msg('startup');

		scroll.init();
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.investorsStockInfoChartHeaderCls || '', 'investors_stock_info_chart', { animCls: 'animate__zoomIn50P', once: true});
		scroll.registerAnimatedElement(this.publicVars.cssId.anim?.investorsStockInfoChartWrapperCls || '', 'investors_stock_info_chart', { animCls: 'animate__fadeIn', once: true});
	}

	bind() {
		this.msg('bind');

		if (this.publicVars.cssId.investorsStockInfoChartButtonCls) {
			const buttons: NodeListOf<HTMLElement> = document.querySelectorAll(this.publicVars.cssId.investorsStockInfoChartButtonCls);

			buttons.forEach(button => {

				if(this.publicVars.cssId.chartStockButtonActiveCls && button.classList.contains(this.publicVars.cssId.chartStockButtonActiveCls)) {
					const ticker = button.dataset['ticker'] as Ticker;

					if(this.privateVars.tickers.includes(ticker)) {
						this.privateVars.currentTicker = ticker;
					}
				}

				button.addEventListener('click', () => {
					const ticker = button.dataset.ticker as Ticker;

					if(this.privateVars.tickers.includes(ticker)) {
						this.redrawChart(ticker);
					}
				});
			});
		}

		this.msg(`Current selected ticker ${this.privateVars.currentTicker}`);

		this.executeChartInit();
	}

	async executeChartInit() {

		const chartWrapperHtml = document.querySelector(this.publicVars.cssId.investorsStockInfoChartWrapperCls || '');

		if (!chartWrapperHtml) {
			this.errorMsg('Please configure chart wrapper class');
			return;
		}

		if (!this.publicVars.apiEndpointPlsrl || !this.publicVars.apiEndpointPlsrv ||
			!this.publicVars.apiEndpointPsrhf) {
			this.errorMsg('Please configure api urls');
			return;
		}

		const dataPlsrl = fetch(this.publicVars.apiEndpointPlsrl);
		const dataPlsrv = fetch(this.publicVars.apiEndpointPlsrv);
		const dataPshrf = fetch(this.publicVars.apiEndpointPsrhf);

		Promise.all([dataPlsrl, dataPlsrv, dataPshrf]).then((values) => {
			const responsePlsrl: Promise<[number, number, number][]> = values[0].json();
			const responsePlsrv: Promise<[number, number, number][]> = values[1].json();
			const responsePshrf: Promise<[number, number, number][]> = values[2].json();

			return Promise.all([responsePlsrl, responsePlsrv, responsePshrf]);
		}).then((values) => {
			const plsrlValues = this.parseApiData(values[0]);
			const plsrvValues = this.parseApiData(values[1]);
			const pshrfValues = this.parseApiData(values[2]);

			this.privateVars.plsrl.data = plsrlValues['stockData'];
			this.privateVars.plsrl.volume = plsrlValues['stockVolume'];

			this.privateVars.plsrv.data = plsrvValues['stockData'];
			this.privateVars.plsrv.volume = plsrvValues['stockVolume'];

			this.privateVars.psrhf.data = pshrfValues['stockData'];
			this.privateVars.psrhf.volume = pshrfValues['stockVolume'];

			// setTimeout(() => {
			chartWrapperHtml.classList.remove(
				this.publicVars.cssId?.investorsStockInfoChartWrapperLoadingCls?.substring(1) || '');
			this.handleChartDraw(this.privateVars[this.privateVars.currentTicker].data, this.privateVars[this.privateVars.currentTicker].volume);
			// }, 2000);
		}).catch(() => {
			this.errorMsg('Fetching api error');
		});
	}

	parseApiData(data: [number, number, number][]) {
		const stockData: [number, number][] = [];
		const stockVolume: [number, number][] = [];
		const dataLength = data.length;

		for (let i = 0; i < dataLength; i += 1) {
			stockData.push([
				data[i][0], // the date
				data[i][1] // close
			]);

			stockVolume.push([
				data[i][0], // the date
				data[i][2] // the volume
			]);
		}

		return { stockData, stockVolume };
	}

	redrawChart(ticker: Ticker) {
		if(!this.privateVars.chart) {
			this.errorMsg('Chart is not available');
			return;
		}

		if(ticker === this.privateVars.currentTicker) {
			return;
		}

		const oldBtnHtml = document.querySelector(this.publicVars.cssId.investorsStockInfoChartButtonCls + '--' + this.privateVars.currentTicker);
		const activeBtnHtml = document.querySelector(this.publicVars.cssId.investorsStockInfoChartButtonCls + '--' + ticker);

		if(oldBtnHtml) {
			oldBtnHtml.classList.remove(this.publicVars.cssId?.chartStockButtonActiveCls?.substring(1) || '');
		}

		if(activeBtnHtml) {
			activeBtnHtml.classList.add(this.publicVars.cssId?.chartStockButtonActiveCls?.substring(1) || '');
		}

		this.privateVars.currentTicker = ticker;

		const TICKER_FORMATS: Record<Ticker, { yAxisFormat: string; tooltipPrefix: string }> = {
			plsrl: { yAxisFormat: '£{value}', tooltipPrefix: '£' },
			plsrv: { yAxisFormat: '${value}', tooltipPrefix: '$' },
			psrhf: { yAxisFormat: '${value}', tooltipPrefix: '$' },
		};

		const currencyFormat = TICKER_FORMATS[ticker]?.yAxisFormat ?? '£{value}';
		const tooltipPrefix = TICKER_FORMATS[ticker]?.tooltipPrefix ?? '£';

		// 1. Update the primary y-axis format (pass false as 2nd parameter to prevent immediate redraw)
		this.privateVars.chart.yAxis[0].update({
			labels: {
				format: currencyFormat
			}
		}, true);

		// 2. (Optional) Also update the price series tooltip prefix/formatting
		this.privateVars.chart.series[0].update({
			type: 'line',
			tooltip: {
				valuePrefix: tooltipPrefix
			}
		}, true);

		const animationConfig = {
			duration: 800,
			easing: 'easeInOutQuad'
		};

		this.privateVars.chart.series[0].setData(
			this.privateVars[ticker].data,
			false,
			animationConfig,
			false
		);

		this.privateVars.chart.series[1].setData(
			this.privateVars[ticker].volume,
			false,
			animationConfig,
			false
		);

		this.privateVars.chart.redraw(animationConfig);
	}

	handleChartDraw(stockData: [number, number][], stockVolume: [number, number][]) {

		this.privateVars.chart = HighchartsStock.stockChart(
			this.publicVars.cssId.investorsStockInfoChartCanvasId?.substring(1) || '', {
				xAxis: {
					tickColor: '#4c4848',      // Changes the tick marks color
					labels: {
						style: {
							color: '#3be380',   // Changes the date label text color to white
							fontFamily: 'source-sans-pro'
						}
					}
				},
				yAxis: [{
					gridLineColor: '#4c4848',
					labels: {
						align: 'left',
						format: `${this.privateVars.currentTicker === 'plsrl' ? '£' : '$'}{value}`,
						style: {
							color: '#3be380',
							fontFamily: 'source-sans-pro'
						}
					},
					height: '80%',
					resize: {
						enabled: true
					}
				}, {
					gridLineColor: '#4c4848',
					labels: {
						align: 'left',
						enabled: false
					},
					top: '80%',
					height: '20%',
					offset: 0,
				}],
				series: [{
					type: 'line',
					id: 'pulsar-line',
					name: 'Price',
					data: stockData,
					getExtremesFromAll: true,
					color: '#3be380',
					tooltip: {
						valuePrefix: this.privateVars.currentTicker === 'plsrl' ? '£' : '$',
						valueDecimals: 3
					}
				}, {
					type: 'column',
					id: 'pulsar-volume',
					name: 'Volume',
					data: stockVolume,
					yAxis: 1,
					color: '#3be380'
				}],
				credits: {
					enabled: false
				},
				chart: {
					backgroundColor: '#000000',
				},

				responsive: {
					rules: [{
						condition: {
							maxWidth: 520 // Exact chart width (in px) where dropdown kicks in
						},
						chartOptions: {
							rangeSelector: {
								dropdown: 'always' // Forces select dropdown list when <= 600px
							},
						}
					}, {
						condition: {
							maxWidth: 400 // Exact chart width (in px) where dropdown kicks in
						},
						chartOptions: {
							rangeSelector: {
								inputEnabled: false // Hides the From/To date input fields on small screens
							}
						}
					}]
				},

				navigator: {
					enabled: true,
					outlineColor: '#4c4848',          // Border of the entire navigator container
					outlineWidth: 1,
					maskFill: 'rgba(59, 227, 128, 0.15)', // Color overlay for the selected range area
					handles: {
						backgroundColor: '#3be380',   // Left and right drag handle fill color
						borderColor: '#ffffff'        // Handle border color
					},
					series: {
						color: '#3be380',             // Line color of the mini chart
						fillColor: 'rgba(59, 227, 128, 0.05)' // Fill color under the mini chart line
					},
					xAxis: {
						gridLineColor: '#2b2b2b',     // Grid lines in navigator
						showLastLabel: false,
						labels: {
							style: {
								color: '#ffffff',      // Navigator date label text color
								fontFamily: 'source-sans-pro',
							}
						}
					},
				},

				scrollbar: {
					barBackgroundColor: '#2a2a2a',    // Scrollbar slider color
					barBorderColor: '#4c4848',
					buttonBackgroundColor: '#1f1f1f', // Scrollbar left/right arrows background
					buttonBorderColor: '#4c4848',
					buttonArrowColor: '#ffffff',      // Arrow icons color
					rifleColor: '#3be380',            // The lines/grip inside the scrollbar thumb
					trackBackgroundColor: '#0f0f0f',  // Scrollbar track background
					trackBorderColor: '#222222'
				},

				rangeSelector: {
					enabled: true,
					// Label text ("Zoom", "From", "To")
					labelStyle: {
						color: '#ffffff',
						fontFamily: 'source-sans-pro'
					},
					// Buttons styling
					buttonTheme: {
						fill: '#000000',              // Button background color (normal state)
						stroke: '#4c4848',            // Button border color
						'stroke-width': 1,
						r: 4,                         // Corner radius
						style: {
							color: '#cccccc',         // Button text color
							fontWeight: 'bold',
							fontFamily: 'source-sans-pro',
						},
						states: {
							hover: {
								fill: '#333333',
								style: {
									color: '#ffffff'
								}
							},
							select: {
								fill: '#3be380',      // Active/selected button background
								stroke: '#3be380',
								style: {
									color: '#000000'  // Active button text color
								}
							},
							disabled: {
								style: {
									color: '#555555'
								}
							}
						}
					},
					// Date inputs ("From / To" date boxes)
					inputBoxBorderColor: 'transparent',
					inputStyle: {
						color: '#ffffff',
						fontFamily: 'source-sans-pro',
						backgroundColor: '#1a1a1a',
						colorScheme: 'dark',
						padding: '5px',
						lineHeight: '1.2',
						outline: 'none',
						border: '1px solid transparent'
					}
				},
			}
		);

	}
}

export default Chart;